import type { Contribution } from '../api/types';

export interface ThreadedContributionData {
  /**
   * Every visible contribution, deduped, in input order.
   *
   * Use this — not `roots` — for anything that answers "what is in this
   * thread": phase membership, counts, author sets, the timeline. `roots` is
   * about reply STRUCTURE and is a function of what has paginated in so far,
   * so treating it as "everything" silently hid every riff.
   */
  ordered: Contribution[];
  roots: Contribution[];
  childrenByParentId: Map<string, Contribution[]>;
  contributionById: Map<string, Contribution>;
}

export function buildThreadedContributionData(
  input: Contribution[],
): ThreadedContributionData {
  const seen = new Set<string>();
  const contributions: Contribution[] = [];
  for (const contribution of input) {
    if (seen.has(contribution.id)) continue;
    seen.add(contribution.id);
    contributions.push(contribution);
  }

  const contributionById = new Map<string, Contribution>(
    contributions.map((contribution) => [contribution.id, contribution]),
  );

  const indexById = new Map<string, number>(
    contributions.map((contribution, index) => [contribution.id, index]),
  );

  const childrenByParentId = new Map<string, Contribution[]>();
  for (const contribution of contributions) {
    if (!contribution.buildsOnContributionId) continue;
    const parentId = contribution.buildsOnContributionId;
    const children = childrenByParentId.get(parentId) ?? [];
    children.push(contribution);
    childrenByParentId.set(parentId, children);
  }

  for (const [parentId, children] of childrenByParentId.entries()) {
    children.sort((left, right) => {
      const leftIndex = indexById.get(left.id) ?? 0;
      const rightIndex = indexById.get(right.id) ?? 0;
      return leftIndex - rightIndex;
    });
    childrenByParentId.set(parentId, children);
  }

  const roots = contributions.filter((contribution) => {
    const parentId = contribution.buildsOnContributionId;
    if (!parentId) return true;
    return !contributionById.has(parentId);
  });

  return {
    ordered: contributions,
    roots,
    childrenByParentId,
    contributionById,
  };
}

/**
 * Bucket key for takes carrying no phase stamp. Deliberately not a phase-shaped
 * string: custom templates let a creator name a phase "General", and that phase
 * has to keep its own section, its canonical slot, and its prompt header rather
 * than being absorbed into the unstamped bucket. The NUL prefix makes collision
 * impossible: the server stamps `templatePhase` as `name.trim().slice(0, 50)`,
 * which can never put a control character in front.
 */
export const UNPHASED_GROUP_KEY = '\u0000unphased';

/** What the unstamped bucket is called on screen. */
export const UNPHASED_GROUP_LABEL = 'General';

export interface PhaseGroup {
  /** Stable identity: the stamped templatePhase, or `UNPHASED_GROUP_KEY`. */
  key: string;
  /** Display name — the stamped phase name, or "General" when unstamped. */
  phase: string;
  contributions: Contribution[];
  authors: Map<string, string>; // authorId -> displayName
}

/**
 * Groups contributions by templatePhase. Contributions with null templatePhase
 * go into the unstamped ("General") bucket.
 *
 * Takes EVERY visible contribution, not just roots. A riff is stamped with the
 * phase it was made in, which is not always its parent's — and a phase section
 * is answering "what did people say to this prompt", so a riff belongs in its
 * own phase whether or not its parent happens to be loaded. Grouping roots only
 * hid every riff from the phase view (a riff whose parent was on another page
 * appeared and then vanished as you paged), left contributors who had only ever
 * riffed out of the author stack, and made these counts disagree with the
 * inventory's, which counts all visible takes.
 *
 * Without `canonicalPhaseOrder` the groups keep the phase order of first
 * appearance (historic behavior). With it — an array of templatePhaseKeys in
 * template order, e.g. from the phase inventory — the groups sort: unstamped
 * first, then phases by canonical position, then unknown keys (renamed-phase
 * strays) last in first-appearance order.
 */
export function groupByPhase(
  contributions: Contribution[],
  canonicalPhaseOrder?: string[],
): PhaseGroup[] {
  const groupMap = new Map<string, PhaseGroup>();
  const order: string[] = [];

  for (const contribution of contributions) {
    const key = contribution.templatePhase ?? UNPHASED_GROUP_KEY;
    const authorId = contribution.authorId ?? '__anonymous__';
    const displayName = contribution.author?.displayName ?? 'Anonymous';
    const existing = groupMap.get(key);
    if (existing) {
      existing.contributions.push(contribution);
      if (!existing.authors.has(authorId)) {
        existing.authors.set(authorId, displayName);
      }
    } else {
      groupMap.set(key, {
        key,
        phase: contribution.templatePhase ?? UNPHASED_GROUP_LABEL,
        contributions: [contribution],
        authors: new Map([[authorId, displayName]]),
      });
      order.push(key);
    }
  }

  if (!canonicalPhaseOrder || canonicalPhaseOrder.length === 0) {
    return order.map((key) => groupMap.get(key)!);
  }

  const canonicalPosition = new Map<string, number>();
  canonicalPhaseOrder.forEach((key, index) => {
    if (!canonicalPosition.has(key)) canonicalPosition.set(key, index);
  });

  // Three partitions, each preserving first-appearance order internally; only
  // the known partition re-sorts, by canonical position.
  const unphased: string[] = [];
  const known: string[] = [];
  const unknown: string[] = [];
  for (const key of order) {
    if (key === UNPHASED_GROUP_KEY) unphased.push(key);
    else if (canonicalPosition.has(key)) known.push(key);
    else unknown.push(key);
  }
  known.sort((a, b) => canonicalPosition.get(a)! - canonicalPosition.get(b)!);

  return [...unphased, ...known, ...unknown].map((key) => groupMap.get(key)!);
}

/**
 * Determines if two consecutive contributions should be visually merged (Discord-style).
 * Merges when same author and within 5 minutes.
 */
export function shouldMergeWithPrevious(
  current: Contribution,
  previous: Contribution | undefined,
): boolean {
  if (!previous) return false;
  if ((current.authorId ?? '__a__') !== (previous.authorId ?? '__b__')) return false;
  const currentTime = new Date(current.createdAt).getTime();
  const previousTime = new Date(previous.createdAt).getTime();
  return Math.abs(currentTime - previousTime) < 5 * 60 * 1000;
}
